import React from 'react';

import { useStore } from '@tanstack/react-store';

import { isNotEmpty } from '@utils/helpers';

/**
 * Хук для определения состояния обработки формы (отправка или валидация)
 */
export const useFieldProcessing = (field: {
  form: {
    store: Parameters<typeof useStore>[0];
  };
}): boolean => {
  const formState = useStore(
    field.form.store,
    (state: { isSubmitting: boolean; isValidating: boolean }) => ({
      isSubmitting: state.isSubmitting,
      isValidating: state.isValidating,
    }),
  );

  return formState.isSubmitting || formState.isValidating;
};

/**
 * Хук для получения начального значения поля
 */
export const useFieldDefaultValue = <TValue = unknown>(field: {
  name: string;
  form: {
    getFieldMeta: (name: string) => unknown;
  };
}): TValue | undefined => {
  return React.useMemo(() => {
    const meta = field.form.getFieldMeta(field.name);
    if (!meta) return;

    // TanStack Form возвращает AnyFieldMeta, но initialValue существует в реальности
    // Используем type assertion через unknown для безопасного приведения типа
    return (meta as unknown as { initialValue?: TValue }).initialValue;
  }, [field.form, field.name]);
};

/**
 * Хук для получения текста ошибки поля
 */
export const useFieldHelperText = (
  field: {
    state: {
      meta: {
        isTouched: boolean;
        errors: Array<string | { message?: string }>;
      };
    };
  },
  defaultText = '',
): string => {
  return React.useMemo(() => {
    if (field.state.meta.isTouched && isNotEmpty(field.state.meta.errors)) {
      const error = field.state.meta.errors[0];
      return typeof error === 'string'
        ? error
        : (error?.message as string) || '';
    }
    return defaultText;
  }, [field.state.meta.isTouched, field.state.meta.errors, defaultText]);
};

/**
 * Хук для форматирования имени поля (заменяет точки на подчёркивания)
 */
export const useFormattedFieldName = (field: { name: string }): string => {
  return React.useMemo(() => field.name.replaceAll('.', '_'), [field.name]);
};
